// Docs · Getting started: Introduction, Quick start, Project structure, Before you code.
import { C, Code, DocLink, Flow, H2, H3, Lead, List, Note, P, Steps, Table } from '../parts'

export function Introduction() {
  return (
    <>
      <Lead>
        The Lender Hub connects Firstfund to each lender’s API. For every lender you write a small piece of code that turns our standard applicant into the
        lender’s request, and the lender’s reply into one of our standard answers. This documentation explains how, from the first file to production.
      </Lead>
      <H2>What you build</H2>
      <P>Every lender has its own API: its own addresses, fields, codes and login. Our web app does not want to know any of that. So for each lender you write:</P>
      <List>
        <li><b>Endpoints</b>: one JavaScript file per lender call (dedupe check, create lead, get offer, status…). Each file reads our applicant, calls the lender once (or a few times) and returns a standard <b>outcome</b> such as <C>offer</C>, <C>rejected</C> or <C>error</C>.</li>
        <li><b>A batch</b>: all endpoint files of one lender together, a small Node.js service with a version number (<C>1.0.0</C>, <C>1.1.0</C>…).</li>
        <li><b>A workflow</b>: the order of the endpoints, drawn on a canvas (dedupe → create lead → get offer → answer).</li>
        <li><b>A service endpoint</b>: the address the web app calls for one product of the lender, e.g. <C>moneyview.personal</C>.</li>
      </List>
      <H2>How one application travels</H2>
      <Flow items={[['Web app', 'POST /v1/applications'], ['Service endpoint', 'picks the workflow version'], ['Workflow', 'step by step'], ['Your endpoint code', 'in the lender’s container'], ['Lender API', 'UAT or production']]} />
      <P>The answer goes back the same way. The web app gets one <b>standard answer</b>, whichever lender it was: <C>offer</C> with a list of offers, <C>redirect</C> with a link, <C>rejected</C>, <C>pending</C> and so on.</P>
      <H2>Where you write the code</H2>
      <P>
        In the <b>playground</b> (lender → tab 3 · Lender API workflow → 3.3 Playground). It is like VS Code in the browser, with your own container (the
        <b> sandbox</b>) where the code really runs and really calls the lender’s UAT. Your code uses one package, the <DocLink to="sdk">Lender SDK</DocLink>{' '}
        (<C>@firstfund/lender-sdk</C>), which is already installed.
      </P>
      <H2>The life of a version</H2>
      <Table code={false} head={['Status', 'What it means', 'What you can do']} rows={[
        [<b>draft</b>, 'being written', 'edit, run, save, add test events'],
        [<b>tests passed</b>, 'every test event is green', 'Build (admins)'],
        [<b>built</b>, 'an image is made, every test passed inside it; read-only', 'Make live (admins)'],
        [<b>live</b>, 'runs in production', 'only a new version changes it'],
        [<b>previous</b>, 'the one before live, still running', 'Roll back in one step'],
        [<b>retired</b>, 'not used any more', '—'],
      ]} />
      <H2>Where to go next</H2>
      <List>
        <li>New here: <DocLink to="quick-start">Quick start</DocLink> (your first endpoint in 15 minutes).</li>
        <li>Writing code: <DocLink to="sdk">SDK overview</DocLink>, <DocLink to="context">ctx</DocLink>, <DocLink to="outcome">the outcome</DocLink>.</li>
        <li>Copy and change: <DocLink to="recipes">Recipes</DocLink>.</li>
        <li>Something red: <DocLink to="errors">Errors and fixes</DocLink>.</li>
      </List>
    </>
  )
}

export const QUICK = `const { defineApi } = require('@firstfund/lender-sdk')

/** Dedupe check: is this person already a customer of the lender? */
module.exports = defineApi({
  title: 'Dedupe check',
  outcomes: ['ok', 'duplicate', 'error'],
  async handler(applicant, ctx) {
    const reply = await ctx.lender({
      method: 'POST',
      url: ctx.env.LENDER_BASE_URL + '/lead/dedupe',
      headers: { 'X-Api-Key': ctx.secret('LENDER_API_KEY'), 'Content-Type': 'application/json' },
      body: { mobile: applicant.mobile, pan: applicant.pan, partner_ref: ctx.ref },
      timeoutMs: 8000,
    })
    const b = reply.body || {}
    if (reply.status === 200 && b.exists === false) return { outcome: 'ok', reply_code: 'NEW_CUSTOMER' }
    if (reply.status === 200 && b.exists === true) return { outcome: 'duplicate', reply_code: 'EXISTING_CUSTOMER' }
    return { outcome: 'error', reply_code: b.code || 'HTTP_' + reply.status }
  },
})`

export function QuickStart() {
  return (
    <>
      <Lead>Your first endpoint, from nothing to green tests, in about 15 minutes. The example is a dedupe check; change the path and fields to your lender’s.</Lead>
      <Note kind="tip">Need a safe target first? Use <C>https://postman-echo.com</C> as the UAT address and the path <C>/post</C>: it sends back what it gets, no lender is called.</Note>
      <H2>Before you start</H2>
      <List>
        <li>The lender exists in the Hub with its UAT address (tab 1). See <DocLink to="lender-setup">Before you code</DocLink>.</li>
        <li>The lender’s key is in tab 5 · Env variables as <C>LENDER_API_KEY</C> (UAT value).</li>
      </List>
      <H2>Step by step</H2>
      <Steps items={[
        ['Create the API', <>Lender → tab 3 → <b>3.1 Lender APIs</b> → <b>Create API</b>: name “Dedupe check”, method POST, lender path <C>/lead/dedupe</C>, auth “API key header”. The Hub makes the batch’s first version (draft) and the endpoint file.</>],
        ['Open the playground', <><b>3.3 Playground</b> → <b>Open playground</b>. Wait for the green dot at the bottom left: your sandbox is ready.</>],
        ['Write the code', <>Open the endpoint file under <C>src/api/</C> and make it read the lender’s reply (below). <b>Ctrl+S</b> saves (one git commit).</>],
        ['Discover endpoints', <>Top bar → <b>Discover endpoints</b>. The Explorer lists the endpoint with its outcomes; a variable your code reads and the Hub does not have yet appears in tab 5, waiting for a value.</>],
        ['Call the lender', <>Paper-plane icon → <b>My APIs</b> → the endpoint → <b>Send</b> (target “Sandbox: my code”). You see the request, the lender’s raw reply and what your code returned.</>],
        ['Make test events', <>Under the reply: <b>Make a test event</b>, with the expected outcome. Make one for every outcome (ok, duplicate, error). See <DocLink to="test-events">Test events</DocLink>.</>],
        ['Run all tests', <>Top bar → <b>Run all tests</b>. All green → the version is <b>tests passed</b>. Save.</>],
        ['Build and go live', <>An admin presses <b>Build</b>, then puts it in a workflow (3.4) and a service endpoint (3.5), then <b>Make live</b>. See <DocLink to="release">Save, Build, Make live</DocLink>.</>],
      ]} />
      <H2>The code</H2>
      <Code title="src/api/v1/dedupe.js  →  POST /v1/dedupe">{QUICK}</Code>
      <H3>What each part does</H3>
      <Table head={['Part', 'Meaning']} rows={[
        ['defineApi({ … })', 'marks the file as one endpoint; the Hub reads title, outcomes and handler from it'],
        ['outcomes', 'every outcome this endpoint may return; each becomes a line out of the box on the workflow canvas'],
        ['handler(applicant, ctx)', 'runs once per call: applicant = our standard input, ctx = the tools'],
        ['ctx.lender({ … })', 'the call to the lender; in a test event it returns the saved reply instead'],
        ['ctx.env.LENDER_BASE_URL', 'the lender’s address from tab 1 (UAT or production, decided by where the code runs)'],
        ['ctx.secret(\'LENDER_API_KEY\')', 'the key from tab 5, never written in the code'],
        ['return { outcome, reply_code }', 'the standard answer of this step and the lender’s own code'],
      ]} />
    </>
  )
}

const LAYOUT = `echo-lender.batchApi/
├── package.json            "start": "node src/server.js"     written for you
├── README.md
├── src/
│   ├── server.js           starts the service                 written for you, leave it
│   ├── app.js              mounts every endpoint file         written for you, leave it
│   ├── api/                ONE FILE = ONE ENDPOINT             you write these
│   │   ├── v1/
│   │   │   ├── dedupe.js           →  POST /v1/dedupe
│   │   │   ├── create-lead.js      →  POST /v1/create-lead
│   │   │   └── get-offer.js        →  POST /v1/get-offer
│   │   └── status/index.js         →  POST /status
│   └── services/           helpers the endpoints share         you write these
│       └── lender.js               address, login, headers
├── tests/                  test events, one file per endpoint  made by the Hub
│   └── v1.dedupe.replies.json
└── .hub/manifest.json      what Discover found                 made by the Hub`

export function ProjectStructure() {
  return (
    <>
      <Lead>A batch is a normal Node.js project. You only write files in <C>src/api</C> and <C>src/services</C>; the Hub makes and reads the rest.</Lead>
      <H2>The folders</H2>
      <Code lang="text" title="a batch">{LAYOUT}</Code>
      <H2>From file to route</H2>
      <P>The route of an endpoint is its place under <C>src/api</C>, without <C>.js</C>. An <C>index.js</C> takes its folder’s name. Nothing is listed by hand.</P>
      <Table head={['File', 'Route']} rows={[
        ['src/api/v1/dedupe.js', 'POST /v1/dedupe'],
        ['src/api/v2.0.0/get-offer.js', 'POST /v2.0.0/get-offer'],
        ['src/api/status/index.js', 'POST /status'],
        ['src/api/index.js', 'POST /'],
      ]} />
      <P>The method is <C>POST</C> unless the endpoint says <C>method: 'GET'</C> (or PUT, PATCH, DELETE) in <DocLink to="define-api">defineApi</DocLink>. This is how the <b>Hub</b> calls your endpoint; how <b>you</b> call the lender is set in each <C>ctx.lender()</C> call.</P>
      <H2>The files written for you</H2>
      <H3>src/server.js</H3>
      <Code title="src/server.js">{`const app = require('./app')

app.listen(Number(process.env.PORT || 8080))`}</Code>
      <H3>src/app.js</H3>
      <Code title="src/app.js">{`const path = require('node:path')
const { createApp } = require('@firstfund/lender-sdk')

module.exports = createApp({ root: path.join(__dirname, '..') })`}</Code>
      <P><DocLink to="create-app">createApp</DocLink> reads every file under <C>src/api</C> and serves it. Leave both files as they are.</P>
      <H2>Rules for the folders</H2>
      <List>
        <li>Every <C>.js</C> file under <C>src/api</C> must end with <C>module.exports = defineApi(…)</C>. A helper there is a problem (red in the Explorer): put helpers in <C>src/services</C>.</li>
        <li>Two files with the same route (e.g. <C>v1/a.js</C> and <C>v1/a/index.js</C>) are a problem: “route is defined twice”.</li>
        <li><C>node_modules</C>, <C>tests</C>, <C>.hub</C> and dot folders are never read as endpoints.</li>
        <li>Packages: Node 22 already has <C>fetch</C>, <C>crypto</C>, <C>Buffer</C>, <C>URLSearchParams</C>. If you really need a package, add it to <C>dependencies</C> in <C>package.json</C>; Build installs it.</li>
      </List>
    </>
  )
}

export function LenderSetup() {
  return (
    <>
      <Lead>What the lender and the Hub need before the first line of code.</Lead>
      <H2>From the lender</H2>
      <List>
        <li>The <b>API document</b>: every endpoint, its fields, its replies and error codes.</li>
        <li>The <b>UAT address</b> and <b>UAT keys</b> (later the production ones).</li>
        <li>The <b>IP whitelist</b>: the lender must allow our server’s IP for UAT and production. Tab 1 shows our IP; in the playground, <b>What is our IP?</b> asks from the sandbox itself. Without the whitelist the calls time out (status <C>0</C>).</li>
      </List>
      <H2>In the Hub</H2>
      <Steps items={[
        ['Add the lender', <>Lenders → <b>Add a lender</b> (one form: name, type, logo, loan information, features, the UTM / Play Store / App Store links). Then on its tab 1 · Lender info set <b>How we connect</b> to API and the <b>UAT</b> and <b>production base addresses</b> (e.g. <C>https://uat-api.lender.com</C>). Your code reads the right one as <C>ctx.env.LENDER_BASE_URL</C>.</>],
        ['Set the Env variables', <>Tab <b>5 · Env variables</b>: each key with its UAT and production value. Names in capitals with <C>_</C>, e.g. <C>LENDER_API_KEY</C>, <C>LENDER_CLIENT_ID</C>, <C>LENDER_CLIENT_SECRET</C>. Values are kept in the vault, shown masked, never in git. See <DocLink to="env-variables">Environment variables</DocLink>.</>],
        ['Create the APIs', <><b>3.1 Lender APIs → Create API</b> once per lender endpoint. It records the method, path and login type, and writes a first file from a template.</>],
      ]} />
      <H2>Login types in Create API</H2>
      <Table head={['Choice', 'The template writes', 'Set in tab 5']} rows={[
        ['bearer', "Authorization: 'Bearer ' + ctx.env.LENDER_TOKEN", 'LENDER_TOKEN (a fixed token), or get it in code: Recipes → Login token'],
        ['api_key_header', "'X-Api-Key': ctx.secret('LENDER_API_KEY')", 'LENDER_API_KEY'],
        ['basic', "Authorization: 'Basic ' + ctx.secret('LENDER_BASIC_AUTH')", 'LENDER_BASIC_AUTH (base64 of user:password)'],
        ['none', 'no login header', '—'],
        ['token_source', 'marks the endpoint that gives a token (the Gateway used it); a workflow never calls it', '—'],
      ]} />
      <Note kind="warning">The Hub does <b>not</b> fetch a lender’s login token by itself. If the token expires, your code gets it: see <DocLink to="recipes#login-token">Recipes → Login token</DocLink>.</Note>
    </>
  )
}
